import { Seat } from "../../../../models/Seat";
import { RowZone } from "../../../../models/RowZone";
import getId from "../../../../../utils/getId";
import { useEffect, useState } from "react";
import {
  BLOCK_SEAT_URL,
  GET_ZONE_SCHEME_URL,
  UNBLOCK_SEAT_URL
} from "../../../../api/apiRoutes";
import { ZoneSeats } from "../../../../models/ZoneSeats";
import useApiRequest from "../../../../../utils/hooks/useApiRequest";
import useFacility from "../../../../../utils/hooks/useFacility";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../../../../store";
import { Exercise } from "../../../../models/Exercise";
import { SelectedSeats } from "../../../../models/SelectedSeats";
import { updateSelectedSeats } from "../../../../store/basket/basketActions";

interface CinemaHallDrawManager {
  loading: boolean;
  cinemaHall: ZoneSeats | null;
  handleSeat: (seat: Seat, rowZone: RowZone) => void;
  changeSeatStatus: (seat: Seat) => Seat;
  seatStausClass: (seat: Seat) => void;
}

export default function useCinemaHallDrawManager(exercise: Exercise): CinemaHallDrawManager {
  const { get, post } = useApiRequest();
  const facility = useFacility();
  const [loading, setLoading] = useState(false);
  const selectedSeats = useSelector((state: State) => state.basket.selectedSeats);
  const dispatch = useDispatch();
  const [cinemaHall, setCinemaHall] = useState<ZoneSeats | null>(null);

  const changeSeatStatus = (seat: Seat) => {
    if (seat.status === "U") {
      return { ...seat, status: "A" };
    }
    if (seat.status === "A") {
      return { ...seat, status: "U" };
    }

    return seat;
  };

  const seatStausClass = (seat: Seat) => {
    if (seat.status === "U") {
      return "selected";
    }
    if (seat.status === "A") {
      return "free";
    }
    if (seat.status === "I") {
      return "busy";
    }
    if (seat.status === "R") {
      return "busy";
    }

    return "free";
  };

  const handleSeat = (seat: Seat, rowZone: RowZone) => {
    if (cinemaHall !== null) {
      if (seat.status === "I" || seat.status === "R") {
        return;
      } else {
        addSeat(seat, rowZone);
      }
    }
  };

  const addSeat = (seat: Seat, row: RowZone) => {
    if (seat.status === "A") {
      blockSeat(seat, row);
    } else if (seat.status === "U") {
      unblockSeat(seat, row);
    }
  };

  const blockSeat = async (seat: Seat, row: RowZone) => {
    const { isError } = await post(BLOCK_SEAT_URL, {
      facilityIds: facility,
      exerciseIds: getId(exercise),
      seatIds: getId(seat),
    });
    if (!isError) {
      const selectSeat = {
        exercise,
        row,
        seat: changeSeatStatus(seat),
      } as SelectedSeats;

      dispatch(updateSelectedSeats([selectSeat, ...selectedSeats]));
      updateCinemaHall(changeSeatStatus(seat), row);
    }
  };

  const unblockSeat = async (seat: Seat, row: RowZone) => {
    const { isError } = await post(UNBLOCK_SEAT_URL, {
      facilityIds: facility,
      exerciseIds: getId(exercise),
      seatIds: getId(seat),
    });
    if (!isError) {
      dispatch(updateSelectedSeats(selectedSeats.filter((item) => getId(item.seat) !== getId(seat))));
      updateCinemaHall(changeSeatStatus(seat), row);
    }
  };

  const updateCinemaHall = (seat: Seat, rowZone: RowZone) => {
    if (cinemaHall !== null) {
      setCinemaHall({
        ...cinemaHall,
        rowZones: cinemaHall.rowZones.map((row) =>
          getId(row.rowZone) === getId(rowZone)
            ? { ...row, seats: row.seats.map((item) => (getId(item) === getId(seat) ? seat : item)) }
            : row
        ),
      });
    }
  };

  useEffect(
    () => {
      const getZoneScheme = async () => {
        setLoading(true);
        const { result, isError } = await get(GET_ZONE_SCHEME_URL(facility, getId(exercise)));
        if (!isError) {
          setCinemaHall(result);
        }
        setLoading(false);
      };
      getZoneScheme();
    }, //eslint-disable-next-line
    [getId(exercise)]
  );

  return {
    loading,
    cinemaHall,
    handleSeat,
    changeSeatStatus,
    seatStausClass,
  };
}
